'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { Box, Modal, ModalHeader, ModalBody, ModalFooter, Button, Text, useTokens } from '@mezzoforte/forge';
import { Global, css } from '@emotion/react';
import { useSession } from '@/hooks/useSession';
import { FreshchatScript } from '@/features/Freshchat/FreshchatScript';
import Icon from '@/features/Freshchat/ChatTeardrop.svg';
import { useCookieConsent } from '@/hooks/useCookieConsent';

const { breakpoints } = useTokens.huutokaupat;

function ConsentWarningModal({ onClose }: { onClose: () => void }) {
  const [isOpen, setIsOpen] = useState(true);
  const { renewCookieConsent } = useCookieConsent();

  const onDismiss = () => {
    setIsOpen(false);
    onClose();
  };

  const onRenewConsentClick = useCallback(() => {
    onClose();
    renewCookieConsent();
  }, [renewCookieConsent, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
    >
      <ModalHeader>Käy hyväksymässä evästeet</ModalHeader>
      <ModalBody>
        <Text>
          Chattia käyttääksesi sinun tulee hyväksyä <strong>Toiminnalliset evästeet</strong>.
        </Text>
        <Button
          my={2}
          onClick={onRenewConsentClick}
        >
          Muokkaa evästeasetuksia
        </Button>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onDismiss}>Sulje</Button>
      </ModalFooter>
    </Modal>
  );
}

export function FreshchatButton() {
  const [chatInitialized, setChatInitialized] = useState(typeof window !== 'undefined' && 'fcWidget' in window);
  const [chatOpen, setChatOpen] = useState((typeof window !== 'undefined' && window.fcWidget?.isOpen()) ?? false);
  const {
    currentUser: { data: session },
  } = useSession();
  const { cookieConsent } = useCookieConsent();

  useEffect(() => {
    if (chatOpen && window.fcWidget?.isOpen() === false) window.fcWidget?.open();
  }, [chatOpen]);

  useEffect(() => {
    function onHideChat() {
      setChatOpen(false);
    }

    if (chatInitialized) {
      window.fcWidget?.on('widget:closed', onHideChat);
    }
    return () => {
      window.fcWidget?.off('widget:closed', onHideChat);
    };
  }, [chatInitialized]);

  const toggleChat = () => {
    setChatOpen((val) => !val);
  };

  if (session?.user === null || session?.user?.isAdmin === false)
    return (
      <>
        <FreshchatScript onLoad={() => setChatInitialized(true)} />
        <Global
          styles={css`
            @media screen and (max-width: ${breakpoints.lg}) {
              #fc_frame {
                bottom: 100px !important;
              }
            }
          `}
        />
        {chatOpen && !cookieConsent.freshchat && <ConsentWarningModal onClose={() => setChatOpen(false)} />}
        {!chatOpen && (
          <Box
            as="button"
            onClick={toggleChat}
            position="fixed"
            right="10px"
            bottom={{ base: 'calc(70px + env(safe-area-inset-bottom))', lg: '10px' }}
            zIndex="sticky"
            sx={{ img: { width: { base: '48px', lg: '60px' } } }}
          >
            <Image
              alt="Asiakaspalvelu"
              src={Icon} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
              width={60}
              height={60}
            />
          </Box>
        )}
      </>
    );

  return null;
}
